import 'shared/styles/style.less';
import React, { useEffect } from 'react';
import {
  Redirect,
  Route,
  RouteProps,
  Switch,
  matchPath,
  useLocation,
} from 'react-router';
import loadable, { LoadableComponent } from '@loadable/component';
import AuthRoute from 'browser/components/AuthRoute';
import { Template, TemplateProps } from 'browser/components/Template';
import ErrorWrap from 'shared/components/ErrorWrap';
import Loader from 'shared/components/Loader';
import { selectActiveUser } from 'shared/store/auth-slice';
import { useAppSelector } from 'shared/store';
import { selectAuthedAccount } from 'shared/store/shared-selectors';

const isDev = process.env.NODE_ENV === 'development';
const isStaging = window.location.host.includes('staging');
const isDevOrStaging = isDev || isStaging;

// wrap components in loadable...import & they will be split
// Make sure you specify chunkname! Must replace slashes with dashes.
const opts = { fallback: <Loader size="large" /> };
const Auth = loadable(() => import('./pages/auth'));
const Dashboard = loadable(() => import('./pages/dashboard'), opts);
const Engage = loadable(() => import('./pages/engage'), opts);
const Replay = loadable(() => import('./pages/replay'), opts);
const Analytics = loadable(() => import('./pages/analytics'));
const Automations = loadable(() => import('./pages/automations'));
const RepAI = loadable(() => import('./pages/repai'));
const RepAICompareVersion = loadable(() => import('./pages/repai-compare-version'));
const RepAIGoal = loadable(() => import('./pages/repai-goal'));
const HelpCenter = loadable(() => import('./pages/help-center'), opts);
const Article = loadable(() => import('./pages/help-center-article-edit'), opts);
const Spotlight = loadable(() => import('./pages/spotlight'), opts);
const Scheduling = loadable(() => import('./pages/scheduling'), opts);
const Onboarding = loadable(() => import('./pages/onboarding'), opts);
const Settings = loadable(() => import('./pages/settings'), opts);
const Referrals = loadable(() => import('./pages/referrals'), opts);
const Exception = loadable(() => import('./pages/exception'), opts);
const VerifyEmail = loadable(() => import('./pages/email-verify'), opts);
const RecoverEmail = loadable(() => import('./pages/email-recover') as any, opts);
const UnsubscribeEmail = loadable(() => import('./pages/email-unsubscribe'), opts);
const AcceptInvitation = loadable(() => import('./pages/accept-invitation'), opts);
const OAuthCallback = loadable(() => import('./pages/oauth-callback'), opts);
const UserOAuthCallback = loadable(() => import('./pages/user-oauth-callback'), opts);
const SSOCallback = loadable(() => import('./pages/sso-callback'), opts);
const OAuthInstall = loadable(() => import('./pages/oauth-install'), opts);
const Zendesk = loadable(() => import('./pages/zendesk'), opts);
const Outbound = loadable(() => import('./pages/outbound'), opts);
const Salesfloor = loadable(() => import('./pages/salesfloor'), opts);
const SalesfloorRoom = loadable(() => import('./pages/salesfloor-room'), opts);
const SalesfloorLinkedInCallback = loadable(
  () => import('./pages/salesfloor-linkedin-callback'),
  opts,
);
const LiveListen = loadable(() => import('./pages/live-listen'), opts);
const InternalAuthImpersonate = loadable(
  () => import('./pages/internal-auth-impersonate'),
  opts,
);
const Playground = loadable(() => import('./pages/playground'), opts);
const AgentScheduling = loadable(() => import('./pages/agent-scheduling'), opts);
const Extension = loadable(() => import('./pages/extension'), opts);

interface RouteConfig extends RouteProps {
  route: RouteProps;
  template: TemplateProps;
  preload?: LoadableComponent<{}>[];
  meta?: { [key: string]: any };
  requiresAuth?: boolean;
  onlyLoggedIn?: boolean;
  onlyLoggedOut?: boolean;
  loggedInOrPublic?: boolean;
  onlyDevOrStaging?: boolean;
}

export const routes: RouteConfig[] = [
  {
    // Settings page
    route: {
      path: [
        '/settings',
        '/settings/:page',
        '/settings/:page(proactive-videos)/add',
        '/settings/:page(proactive-videos)/edit/:id',
        '/settings/:page(user-groups|segments|journeys)/:id',
        '/settings/:page(designer)/:designerPage',
        '/settings/:page(designer)/:designerPage(display-rules)/add',
        '/settings/:page(designer)/:designerPage(display-rules)/edit/:id',
      ],
      component: Settings,
      exact: true,
    },
    template: {
      title: 'Settings',
    },
    onlyLoggedIn: true,
  },
  {
    route: {
      path: '/referrals',
      component: Referrals,
      exact: true,
    },
    template: {
      title: 'Referrals',
    },
    onlyLoggedIn: true,
  },
  {
    route: {
      path: ['/', '/engage/widget-setup'],
      component: Engage,
      exact: true,
    },
    template: {
      title: 'Engage',
    },
    onlyLoggedIn: true,
  },
  {
    route: {
      path: ['/visitor', '/visitor/:visitorId'],
      component: Dashboard,
      exact: true,
    },
    template: {
      title: 'Dashboard',
    },
    onlyLoggedIn: true,
  },
  {
    route: {
      path: ['/replay/:sessionGroup', '/replay/:sessionGroup/:recordingId'],
      component: Replay,
      exact: true,
    },
    template: {
      title: 'Session Replay',
      hideHeader: true,
    },
  },
  {
    route: {
      path: [
        '/analytics',
        '/analytics/:page',
        '/analytics/:page(activity)/:activityTab',
        '/analytics/:page(activity)/:activityTab(sessions)/:sessionGroup/recording',
        '/analytics/:page(activity)/:activityTab(sessions)/:sessionGroup/recording/:recordingId',
        '/analytics/:page(activity)/:activityTab/:dialTaskId/recording',
      ],
      component: Analytics,
      exact: true,
    },
    template: {
      title: 'Analytics',
    },
    onlyLoggedIn: true,
  },
  {
    route: {
      path: ['/automations', '/automations/:page', '/automations/:page/:automationId'],
      component: Automations,
      exact: true,
    },
    template: {
      title: 'Automations',
    },
    onlyLoggedIn: true,
  },
  {
    route: {
      path: ['/ai/goals/:id'],
      component: RepAIGoal,
      exact: true,
    },
    template: {
      title: 'AI Goals',
    },
    onlyLoggedIn: true,
  },
  {
    route: {
      path: ['/ai/ai-training/scope/review'],
      component: RepAICompareVersion,
      exact: true,
    },
    template: {
      title: 'Compare AI Scope Versions',
    },
    onlyLoggedIn: true,
  },
  {
    route: {
      path: ['/ai', '/ai/:tab', '/ai/:tab/:subtab'],
      component: RepAI,
      exact: true,
    },
    template: {
      title: 'AI',
    },
    onlyLoggedIn: true,
  },
  {
    // Help Center page
    route: {
      path: ['/help-center'],
      component: HelpCenter,
      exact: true,
    },
    template: {
      title: 'Help Center',
    },
    onlyLoggedIn: true,
  },
  {
    route: {
      path: ['/help-center/articles/add', '/help-center/articles/:articleId'],
      component: Article,
      exact: true,
    },
    template: {
      title: 'Help Center Article',
    },
    onlyLoggedIn: true,
  },

  {
    route: {
      path: [
        '/spotlight',
        '/spotlight/:group',
        '/spotlight/preferences',
        '/spotlight/account/:accountId',
        '/spotlight/:group/lists/:list',
        '/spotlight/:group/:page',
      ],
      component: Spotlight,
      exact: true,
    },
    template: {
      title: 'Spotlight',
    },
    onlyLoggedIn: true,
  },
  {
    // Sign in / sign up / recover (nested routes)
    route: {
      path: '/auth',
      component: Auth,
    },
    template: {
      title: 'Sign in',
      hideHeader: true,
      isFullScreen: true,
      isWhite: true,
    },
    preload: [Dashboard],
    onlyLoggedOut: true,
  },
  {
    route: {
      path: '/onboarding',
      component: Onboarding,
    },
    template: {
      title: 'Onboarding',
      hideHeader: true,
      isFullScreen: true,
      isWhite: true,
    },
    onlyLoggedIn: true,
  },
  {
    // Verify email
    route: {
      path: '/email/verify',
      component: VerifyEmail,
      exact: true,
    },
    template: {
      title: 'Verify email',
    },
  },
  {
    // Recover email
    route: {
      path: '/email/recover',
      component: RecoverEmail,
      exact: true,
    },
    template: {
      title: 'Recover email',
    },
  },
  {
    // Unsubscribe email
    route: {
      path: '/email/unsubscribe',
      component: UnsubscribeEmail,
      exact: true,
    },
    template: {
      title: 'Unsubscribe email',
    },
  },
  {
    // Accept invitation
    route: {
      path: '/team/verify',
      component: AcceptInvitation,
      exact: true,
    },
    template: {
      title: 'Accept invitation',
      hideHeader: true,
    },
  },
  {
    // Handle SSO auth callback
    route: {
      path: '/sso/callback',
      component: SSOCallback,
    },
    template: {
      title: 'SSO Authentication',
      hideHeader: true,
      isFullScreen: true,
      isWhite: true,
    },
    preload: [Dashboard],
    onlyLoggedOut: true,
  },
  {
    // Handle Org OAuth callbacks
    route: {
      path: '/oauth/callback/:service',
      component: OAuthCallback,
    },
    template: {
      title: 'Complete OAuth',
    },
    onlyLoggedIn: true,
  },
  {
    // Handle User OAuth callbacks
    route: {
      path: '/user-oauth/callback/:service',
      component: UserOAuthCallback,
    },
    template: {
      title: 'Complete OAuth',
    },
    onlyLoggedIn: true,
  },
  {
    // Trigger OAuth installs
    route: {
      path: '/oauth/install/:service',
      component: OAuthInstall,
    },
    template: {
      title: 'Install add-on',
    },
    onlyLoggedIn: true,
  },
  {
    // Zendesk app
    route: {
      path: ['/zendesk', '/zendesk/:visitorId'],
      component: Zendesk,
      exact: true,
    },
    template: {
      title: 'Zendesk',
      hideHeader: true,
      isFullScreen: true,
    },
  },
  {
    route: {
      path: '/outbound',
      component: Outbound,
      exact: true,
    },
    template: {
      title: 'Dialer',
    },
    onlyLoggedIn: true,
  },
  {
    route: {
      path: '/salesfloor',
      component: Salesfloor,
      exact: true,
    },
    template: {
      title: 'Salesfloor',
    },
    onlyLoggedIn: true,
  },
  {
    route: {
      path: '/livelisten',
      component: LiveListen,
      exact: true,
    },
    template: {
      title: 'Live Listen',
    },
    onlyLoggedIn: true,
  },
  {
    route: {
      path: '/salesfloor/room/:id',
      component: SalesfloorRoom,
      exact: true,
    },
    template: {
      title: 'Salesfloor',
    },
    loggedInOrPublic: true,
  },
  {
    route: {
      path: '/salesfloor/linkedin-callback',
      component: SalesfloorLinkedInCallback,
    },
    template: {
      title: 'Sign in with LinkedIn',
      hideHeader: true,
      isFullScreen: true,
      isWhite: true,
    },
    onlyLoggedOut: true,
  },
  {
    // Internal tool, impersonate auth
    route: {
      path: '/internal/auth-impersonate',
      component: InternalAuthImpersonate,
    },
    template: {
      title: 'Auth impersonate',
    },
  },
  // Agent Meet/Book/Event
  {
    route: {
      path: [
        '/scheduling',
        '/scheduling/:page',
        '/scheduling/:page(event-types)/add',
        '/scheduling/:page(event-types)/edit/:id',
        '/scheduling/:page(event-types)/edit/:id/details',
        '/scheduling/:page(event-types)/edit/:id/hosts',
        '/scheduling/:page(event-types)/edit/:id/questions',
        '/scheduling/:page(event-types)/edit/:id/advanced',
      ],
      component: Scheduling,
      exact: true,
    },
    template: {
      title: 'Schedule',
    },
    onlyLoggedIn: true,
  },
  // Browser extension authentication
  {
    route: {
      path: ['/extension'],
      component: Extension,
      exact: true,
    },
    template: {
      title: 'Browser Extension',
    },
    onlyLoggedIn: true,
  },
  {
    // Playground
    route: {
      path: '/playground',
      component: Playground,
      exact: true,
    },
    template: {
      title: 'Playground',
      isFullScreen: true,
      isWhite: true,
    },
    onlyDevOrStaging: true,
    onlyLoggedIn: isDev ? false : true,
  },
  {
    // Legacy routes, redirect to new scheduling page
    route: {
      path: [
        '/meet/:agentSlug',
        '/meet/:agentSlug/:eventSlug',
        '/meet/:agentSlug/:eventSlug/reschedule/:eventId',
        '/meet/:agentSlug/:eventSlug/cancel/:eventId',
      ],
    },
    meta: {
      redirect: (path: string) => path.split('/meet/').join('/'),
    },
    template: {
      title: 'Redirecting...',
    },
  },
  {
    route: {
      path: [
        '/:agentSlug',
        '/:agentSlug/:eventSlug',
        '/:agentSlug/:eventSlug/reschedule/:eventId',
        '/:agentSlug/:eventSlug/cancel/:eventId',
      ],
      component: AgentScheduling,
      exact: true,
    },
    meta: {
      isAgentPage: true,
    },
    template: {
      title: 'Meet instantly or schedule a meeting',
      hideHeader: true,
      isFullScreen: true,
    },
  },
  {
    // 404
    route: {
      path: '/*',
      render: () => <Exception code="404" />,
    },
    template: {
      title: 'Page not found',
    },
  },
].filter(r => isDevOrStaging || !r.onlyDevOrStaging);

const Routes = () => {
  const location = useLocation();
  const userId = useAppSelector(selectActiveUser)?.id;
  const account = useAppSelector(selectAuthedAccount);
  const hasCheckedUser = useAppSelector(state => state.auth.hasCheckedUser);

  useEffect(() => {
    preloadRoutes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const preloadRoutes = () => {
    // Preload pages that are likely to come next
    const currentRoute = getCurrentRoute();
    currentRoute.preload?.forEach(loadable => loadable.preload());
  };

  const getCurrentRoute = () => {
    const { pathname } = location;
    return (
      routes.find(config => !!matchPath(pathname, config.route)) ||
      routes[routes.length - 1]
    );
  };

  const routeConfigs = [...routes];
  const currentRoute = getCurrentRoute();
  const routeComponents = routeConfigs.map(config => {
    const { route, onlyLoggedIn, onlyLoggedOut, loggedInOrPublic } = config;
    if (onlyLoggedIn || onlyLoggedOut || loggedInOrPublic) {
      return (
        <AuthRoute
          key={route.path as string}
          onlyLoggedOut={onlyLoggedOut}
          loggedInOrPublic={loggedInOrPublic}
          {...route}
        />
      );
    } else {
      if (!hasCheckedUser) {
        return <Loader key={route.path as string} size="large" />;
      }

      return <Route key={route.path as string} {...route} />;
    }
  });

  if (currentRoute.meta?.redirect) {
    return <Redirect to={currentRoute.meta.redirect(location.pathname)} />;
  }

  return (
    <Template {...currentRoute.template} hideHeader={!account} key={userId || 'unauthed'}>
      <ErrorWrap key={currentRoute.route.path as string}>
        <Switch>{routeComponents}</Switch>
      </ErrorWrap>
    </Template>
  );
};

export default Routes;
